import { useEffect, useState } from "react";
import { CalendarCheck, CheckCircle2, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import { getRequests, acceptProposal, apiErr } from "../lib/api";
import { useCustomer } from "../lib/auth";

function ProposalRow({ p, onAccept, accepting }) {
  return (
    <div className="rounded-xl border border-[#EBE0D0] bg-[#FBF6EF] p-3" data-testid={`proposal-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-heading text-sm text-[#2A211C]">{p.priest_name || "Purohit"}</p>
          {p.message ? <p className="mt-0.5 text-xs text-[#7A6E64]">{p.message}</p> : null}
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#A0968A]">
            <span>Dakshina ₹{p.amount}</span>
            <span>{p.includes_samagri ? "Samagri included" : "Samagri not included"}</span>
          </div>
        </div>
        <button
          data-testid={`accept-proposal-${p.id}`}
          onClick={() => onAccept(p.id)}
          disabled={accepting}
          className="shrink-0 rounded-xl bg-[#8B1A2B] px-3.5 py-2 text-xs font-semibold text-white transition-opacity disabled:opacity-50"
        >
          {accepting ? "…" : "Accept"}
        </button>
      </div>
    </div>
  );
}

export default function Bookings() {
  const { user, ready, setAuthOpen } = useCustomer();
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState(null);

  const load = () => {
    getRequests().then((d) => setReqs(d.requests)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    load();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const onAccept = async (reqId, proposalId) => {
    setAcceptingId(proposalId);
    try {
      await acceptProposal(reqId, proposalId);
      toast.success("ಪ್ರಸ್ತಾವನೆ ಸ್ವೀಕರಿಸಲಾಗಿದೆ · Proposal accepted — booking confirmed");
      load();
    } catch (e) {
      toast.error(apiErr(e, "Could not accept proposal"));
    } finally {
      setAcceptingId(null);
    }
  };

  const booked = reqs.filter((r) => r.status === "booked");
  const openReqs = reqs.filter((r) => r.status !== "booked");

  return (
    <div className="mx-auto max-w-5xl px-4 pt-5 pc-fade-in" data-testid="cust-bookings">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B1A2B]">Trips and ceremonies</p>
      <h1 className="mt-1 font-heading text-2xl text-[#2A211C]">Bookings</h1>
      <p className="mt-1 text-sm text-[#7A6E64]">Review purohit proposals, confirm a booking, and track your ceremonies.</p>

      {ready && !user && (
        <div className="mt-6 rounded-2xl border border-[#EBE0D0] bg-white p-6 text-center">
          <p className="text-sm text-[#7A6E64]">Sign in to see your ceremony requests and bookings.</p>
          <button onClick={() => setAuthOpen(true)} data-testid="cust-bookings-signin" className="mt-3 rounded-xl bg-[#8B1A2B] px-5 py-2.5 text-sm font-semibold text-white">Sign in</button>
        </div>
      )}

      {user && !loading && reqs.length === 0 && (
        <div className="mt-6 rounded-2xl border border-[#EBE0D0] bg-white p-8 text-center">
          <CalendarCheck size={28} className="mx-auto text-[#C9BBA8]" />
          <p className="mt-2 font-semibold text-[#2A211C]">No bookings yet</p>
          <p className="mt-1 text-sm text-[#7A6E64]">Your ceremony requests and proposal selections will appear here.</p>
        </div>
      )}

      {/* Confirmed bookings */}
      {user && booked.length > 0 && (
        <section className="mt-5" data-testid="confirmed-bookings">
          <h2 className="text-sm font-semibold text-[#2A211C]">Confirmed · ಖಚಿತಪಡಿಸಿದ</h2>
          <div className="mt-2 space-y-3">
            {booked.map((r) => (
              <div key={r.id} data-testid={`confirmed-${r.id}`} className="rounded-2xl border border-[#3E6B4F]/30 bg-[#F2F7F3] p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg text-[#2A211C]">{r.pooja_name}</h3>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#3E6B4F] px-2.5 py-0.5 text-[11px] font-semibold uppercase text-white">
                    <CheckCircle2 size={12} /> Confirmed
                  </span>
                </div>
                <p className="mt-1 text-sm text-[#5A4A3F]">{r.date} · {r.time} · {r.address}</p>
                <p className="mt-2 text-sm text-[#2A211C]">
                  <Star size={13} className="mr-1 inline text-[#C9A24A]" />
                  {r.accepted_priest_name || "Your purohit"} · Dakshina ₹{r.proposals?.find((p) => p.id === r.accepted_proposal_id)?.amount ?? ""}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Open requests with proposals */}
      {user && openReqs.length > 0 && (
        <section className="mt-6" data-testid="open-requests">
          <h2 className="text-sm font-semibold text-[#2A211C]">Awaiting your choice · ಪ್ರಸ್ತಾವನೆಗಳು</h2>
          <div className="mt-2 space-y-3">
            {openReqs.map((r) => (
              <div key={r.id} data-testid={`cust-booking-${r.id}`} className="rounded-2xl border border-[#EBE0D0] bg-white p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg text-[#2A211C]">{r.pooja_name}</h3>
                  <span className="rounded-full bg-[#F5E9DA] px-2.5 py-0.5 text-[11px] font-semibold uppercase text-[#8B1A2B]">{r.status}</span>
                </div>
                <p className="mt-1 text-sm text-[#7A6E64]">{r.date} · {r.time} · {r.address}</p>

                {(r.proposals?.length || 0) === 0 ? (
                  <div className="mt-3 flex items-center gap-2 rounded-xl bg-[#FBF6EF] px-3 py-2.5 text-xs text-[#A0968A]">
                    <Sparkles size={14} className="text-[#C9A24A]" />
                    Awaiting purohit proposals — you'll be able to choose one here.
                  </div>
                ) : (
                  <div className="mt-3">
                    <p className="mb-2 text-xs font-semibold text-[#8B1A2B]">
                      {r.proposals.length} proposal{r.proposals.length > 1 ? "s" : ""} · choose one to confirm
                    </p>
                    <div className="space-y-2">
                      {r.proposals.map((p) => (
                        <ProposalRow
                          key={p.id}
                          p={p}
                          accepting={acceptingId === p.id}
                          onAccept={(pid) => onAccept(r.id, pid)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
